import type { WidgetAttachment } from '@widgets/shared/types'
import {
  type WebWidgetChannelType,
  type WebWidgetDetailsMinimizedState,
  type WebWidgetDetailsAppearance,
  type WebWidgetDetailsMinimizedStateAlignTo,
  type WebWidgetDetailsEmailFieldName,
  type WebWidgetParsedEmailChannel,
  type WebWidgetParsedSmsChannel as WebWidgetParsedSmsChannelForSettings,
  type WebWidgetParsedCallChannel as WebWidgetParsedCallChannelForSettings,
  type WebWidgetParsedChatChannel as WebWidgetParsedChatChannelForSettings,
  type WebWidgetParsedWhatsappChannel as WebWidgetParsedWhatsappChannelForSettings,
  type WebWidgetParsedFacebookChannel as WebWidgetParsedFacebookChannelForSettings,
  type WebWidgetParsedInstagramChannel as WebWidgetParsedInstagramChannelForSettings,
  type WebWidgetEmailChannel,
  type WebWidgetSmsChannel as WebWidgetSmsChannelForSettings,
  type WebWidgetCallChannel as WebWidgetCallChannelForSettings,
  type WebWidgetChatChannel as WebWidgetChatChannelForSettings,
  type WebWidgetWhatsappChannel as WebWidgetWhatsappChannelForSettings,
  type WebWidgetFacebookChannel as WebWidgetFacebookChannelForSettings,
  type WebWidgetInstagramChannel as WebWidgetInstagramChannelForSettings,
  type WebWidgetDetailsChannelSorting,
  type WebWidgetDetailsPreChatFieldName,
  type WebWidgetPaletteColor,
  WebWidgetThemeColorTypes,
} from '@/data/models/domain/messages/settings/webWidget/types'
import type { TmCountryCode } from '@/services/types'
import type { Nullable } from '@/types'
import type AttachmentFile from '@/data/models/domain/attachmentFile/AttachmentFile'

export type RequiredBy<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>

export type MessengerViewName =
  | 'idle'
  | 'minimized'
  | 'welcome'
  | 'email'
  | 'emailSuccess'
  | 'sms'
  | 'call'
  | 'chat'
  | 'whatsapp'
  | 'facebook'
  | 'instagram'

export type PreChatFormStep = 'message' | 'introduction'

export type MessengerChannel = {
  type: WebWidgetChannelType
  name: string
  description: string
}

export type MessengerFormField<Name> = {
  name: Name
  isRequired: boolean
}

export type SuitableNumber = {
  countryId: TmCountryCode
  phone: string
}

export type WithSuitableNumber<T> = T & {
  suitableNumber: SuitableNumber | null
}

export type MessengerPaletteColorTheme = {
  type: WebWidgetThemeColorTypes.SOLID | WebWidgetThemeColorTypes.GRADIENT
  color: WebWidgetPaletteColor
}

export type MessengerCustomColorTheme = {
  type: WebWidgetThemeColorTypes.CUSTOM
  primaryColor: {
    background: string
    font: string
  }
  buttonColor: {
    background: string
    font: string
  }
}

export type MessengerColorTheme = MessengerPaletteColorTheme | MessengerCustomColorTheme

export const isMessengerPaletteColorTheme = (theme: MessengerColorTheme): theme is MessengerPaletteColorTheme =>
  theme.type === WebWidgetThemeColorTypes.SOLID || theme.type === WebWidgetThemeColorTypes.GRADIENT

export const isMessengerCustomColorTheme = (theme: MessengerColorTheme): theme is MessengerCustomColorTheme =>
  theme.type === WebWidgetThemeColorTypes.CUSTOM

export type MessengerSharedSettings = {
  theme: MessengerColorTheme
  placement: Pick<WebWidgetDetailsMinimizedState, 'alignTo' | 'sideSpacing' | 'bottomSpacing'>
  hasOnlyOneActiveChannel: boolean
}

export type MessengerMinimizedViewSettings = Pick<
  WebWidgetDetailsMinimizedState,
  'icon' | 'buttonText' | 'borderRadius' | 'eyeCatcherText' | 'eyeCatcherDelay'
>

export type MessengerWelcomeViewSettings = {
  greeting: string
  intro: string
  channels: MessengerChannel[]
  logoUrl: string
}

export type MessengerEmailViewSettings = {
  channel: MessengerChannel
  title: string
  description: string
  buttonText: string
  formFields: MessengerFormField<WebWidgetDetailsEmailFieldName>[]
}

export type MessengerSmsViewSettings = {
  channel: MessengerChannel
  suitableNumber: SuitableNumber | null
  title: string
  description: string
  buttonText: string
  showQrCode?: boolean
}

export type MessengerSuccessViewSettings = {
  channel: MessengerChannel
  title: string
  description: string
}

export type MessengerCallViewSettings = {
  channel: MessengerChannel
  suitableNumber: SuitableNumber | null
  title: string
  description: string
  buttonText: string
  showQrCode?: boolean
}

export type MessengerWhatsappViewSettings = {
  channel: MessengerChannel
  suitableNumber: SuitableNumber | null
  title: string
  description: string
  buttonText: string
  showQrCode?: boolean
}

export type MessengerFacebookViewSettings = {
  channel: MessengerChannel
  facebookChatUrl: string
  title: string
  description: string
  buttonText: string
  showQrCode?: boolean
}

export type MessengerInstagramViewSettings = {
  channel: MessengerChannel
  instagramChatUrl: string
  title: string
  description: string
  buttonText: string
  showQrCode?: boolean
}

export type MessengerPreChatViewSettings = {
  channel: MessengerChannel
  messageLabel: string
  messageButtonText: string
  introductionTitle: string
  introductionButtonText: string
  formFields: MessengerFormField<WebWidgetDetailsPreChatFieldName>[]
}

export type MessengerChatViewSettings = {
  channel: MessengerChannel
  autoReplyMessage: string
  rejectMessage: string
  isSatisfactionSurveyShown: boolean
  isHidden: boolean
  offlineMessage: string
}

export type WebWidgetSmsChannel = WithSuitableNumber<Pick<WebWidgetSmsChannelForSettings, 'isActive' | 'uiSettings'>>
export type WebWidgetParsedSmsChannel = WithSuitableNumber<
  Pick<WebWidgetParsedSmsChannelForSettings, 'isActive' | 'uiSettings'>
>

export type WebWidgetCallChannel = WithSuitableNumber<Pick<WebWidgetCallChannelForSettings, 'isActive' | 'uiSettings'>>
export type WebWidgetParsedCallChannel = WithSuitableNumber<
  Pick<WebWidgetParsedCallChannelForSettings, 'isActive' | 'uiSettings'>
>

export type WebWidgetWhatsappChannel = WithSuitableNumber<
  Pick<WebWidgetWhatsappChannelForSettings, 'isActive' | 'uiSettings'>
>
export type WebWidgetParsedWhatsappChannel = WithSuitableNumber<
  Pick<WebWidgetParsedWhatsappChannelForSettings, 'isActive' | 'uiSettings'>
>

export type WebWidgetFacebookChannel = Pick<WebWidgetFacebookChannelForSettings, 'isActive' | 'uiSettings'> & {
  facebookChatUrl: string
}
export type WebWidgetParsedFacebookChannel = Pick<
  WebWidgetParsedFacebookChannelForSettings,
  'isActive' | 'uiSettings'
> & {
  facebookChatUrl: string
}

export type WebWidgetInstagramChannel = Pick<WebWidgetInstagramChannelForSettings, 'isActive' | 'uiSettings'> & {
  instagramChatUrl: string
}
export type WebWidgetParsedInstagramChannel = Pick<
  WebWidgetParsedInstagramChannelForSettings,
  'isActive' | 'uiSettings'
> & {
  instagramChatUrl: string
}

export type WebWidgetChatChannel = WithSuitableNumber<
  Pick<
    WebWidgetChatChannelForSettings,
    | 'isActive'
    | 'uiSettings'
    | 'fieldSettings'
    | 'autoReplyMessage'
    | 'rejectTimeout'
    | 'rejectMessage'
    | 'isSatisfactionSurveyShown'
    | 'isHidden'
  >
>
export type WebWidgetParsedChatChannel = Pick<
  WebWidgetParsedChatChannelForSettings,
  | 'isActive'
  | 'uiSettings'
  | 'fieldSettings'
  | 'autoReplyMessage'
  | 'rejectTimeout'
  | 'rejectMessage'
  | 'isSatisfactionSurveyShown'
  | 'isHidden'
>

// Raw settings from server
export type MessengerSettingsResponse = {
  appearance: string
  channels: {
    [WebWidgetChannelType.EMAIL]: Nullable<WebWidgetEmailChannel>
    [WebWidgetChannelType.SMS]: Nullable<WebWidgetSmsChannel>
    [WebWidgetChannelType.CALL]: Nullable<WebWidgetCallChannel>
    [WebWidgetChannelType.CHAT]: Nullable<WebWidgetChatChannel>
    [WebWidgetChannelType.WHATSAPP]: Nullable<WebWidgetWhatsappChannel>
    [WebWidgetChannelType.FACEBOOK]: Nullable<WebWidgetFacebookChannel>
    [WebWidgetChannelType.INSTAGRAM]: Nullable<WebWidgetInstagramChannel>
  }
  channelSorting: WebWidgetDetailsChannelSorting
  userCountry: TmCountryCode
  logo: Nullable<AttachmentFile>
}

// Parsed settings
export type MessengerSettings = {
  appearance: WebWidgetDetailsAppearance
  channels: {
    [WebWidgetChannelType.EMAIL]: WebWidgetParsedEmailChannel
    [WebWidgetChannelType.SMS]: WebWidgetParsedSmsChannel
    [WebWidgetChannelType.CALL]: WebWidgetParsedCallChannel
    [WebWidgetChannelType.CHAT]: WebWidgetParsedChatChannel
    [WebWidgetChannelType.WHATSAPP]: WebWidgetParsedWhatsappChannel
    [WebWidgetChannelType.FACEBOOK]: WebWidgetParsedFacebookChannel
    [WebWidgetChannelType.INSTAGRAM]: WebWidgetParsedInstagramChannel
  }
  channelSorting: WebWidgetDetailsChannelSorting
  uniqueCode: string
  userCountry: TmCountryCode
  logoUrl: string
}

export type MessengerEmailForm = {
  name: string
  email: string
  message: string
  attachments: WidgetAttachment[]
}

export type MessengerSmsForm = {
  name: string
  phone: string
  country: TmCountryCode
  message: string
}

export type MessengerPreChatForm = {
  name: string
  email: string
  message: string
}

export type MessengerChatForm = {
  message: string
  attachments: WidgetAttachment[]
  replyToMessageId?: string
}

export type MessengerChatSurveyCommentForm = {
  comment: string
}

export type MessengerResizePayload = {
  width: number
  height: number
  startButtonSize: {
    width: number
    height: number
  }
}

export interface MessengerPosition {
  alignTo: `${WebWidgetDetailsMinimizedStateAlignTo}`
  sideSpacing: number
  bottomSpacing: number
  width: number
  height: number
  isMinimized: boolean
  startButtonSize: {
    width: number
    height: number
  }
}

export interface MessengerParentSize {
  isMobile: boolean
  isParentSmaller: boolean // Parent width smaller than widget
}

export interface QrCodeContactData {
  phoneNumber?: SuitableNumber | null
  href: string
}
